import { createRouter, createWebHistory } from 'vue-router'
import { useToast } from 'vue-toastification'
import { useAuthStore } from '@/stores/auth'

const roleLabels = {
  requester: 'Aanvrager',
  manager: 'Manager',
  director: 'Director',
  administrator: 'Beheerder'
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      redirect: () => {
        // Dynamic redirect based on role - will be handled in beforeEach
        return '/k-numbers'
      }
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/auth/LoginView.vue'),
      meta: { guest: true }
    },
    {
      path: '/auth/callback/:provider',
      name: 'auth-callback-provider',
      component: () => import('@/views/auth/AuthCallback.vue'),
      meta: { guest: true }
    },
    {
      path: '/auth/callback',
      name: 'auth-callback',
      component: () => import('@/views/auth/AuthCallback.vue'),
      meta: { guest: true }
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      component: () => import('@/views/DashboardView.vue'),
      meta: { requiresAuth: true, roles: ['administrator'] }
    },
    {
      path: '/k-numbers',
      name: 'k-numbers',
      component: () => import('@/views/knumbers/KNumberList.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/k-numbers/new',
      name: 'k-number-create',
      component: () => import('@/views/knumbers/KNumberCreate.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/k-numbers/:id',
      name: 'k-number-detail',
      component: () => import('@/views/knumbers/KNumberDetail.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/k-numbers/:id/edit',
      name: 'k-number-edit',
      component: () => import('@/views/knumbers/KNumberEdit.vue'),
      meta: { requiresAuth: true }
      // Access control is handled within the component based on K-number status and ownership
    },
    {
      path: '/approvals',
      name: 'approvals',
      component: () => import('@/views/approvals/ApprovalList.vue'),
      meta: { requiresAuth: true, roles: ['manager', 'director', 'administrator'] }
    },
    {
      path: '/admin',
      name: 'admin',
      component: () => import('@/views/admin/AdminDashboard.vue'),
      meta: { requiresAuth: true, roles: ['administrator'] }
    },
    {
      path: '/admin/users',
      name: 'admin-users',
      component: () => import('@/views/admin/UserManagement.vue'),
      meta: { requiresAuth: true, roles: ['administrator'] }
    },
    {
      path: '/admin/cost-centers',
      name: 'admin-cost-centers',
      component: () => import('@/views/admin/CostCenterManagement.vue'),
      meta: { requiresAuth: true, roles: ['administrator'] }
    },
    {
      path: '/admin/departments',
      name: 'admin-departments',
      component: () => import('@/views/admin/DepartmentManagement.vue'),
      meta: { requiresAuth: true, roles: ['administrator'] }
    },
    {
      path: '/profile',
      name: 'profile',
      component: () => import('@/views/ProfileView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/views/NotFound.vue')
    }
  ]
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  // Handle home route redirect based on role
  if (to.name === 'home' && authStore.isAuthenticated && authStore.user) {
    const defaultRoute = ['manager', 'director', 'administrator'].includes(authStore.user.role) ? 'approvals' : 'k-numbers'
    return next({ name: defaultRoute })
  }

  // Capture ?token=... on any route and finalize login
  if (to.query && to.query.token) {
    try {
      authStore.setToken(to.query.token)
      await authStore.fetchCurrentUser()
      // Redirect managers/directors/admins to approvals, others to k-numbers
      const defaultRoute = ['manager', 'director', 'administrator'].includes(authStore.user?.role) ? 'approvals' : 'k-numbers'
      return next({ name: defaultRoute })
    } catch (e) {
      authStore.setToken(null)
      return next({ name: 'login' })
    }
  }

  // Check if route requires authentication
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!authStore.isAuthenticated) {
      // Store intended route
      authStore.setRedirectUrl(to.fullPath)
      next({ name: 'login' })
      return
    }

    // Ensure user data is loaded before checking roles
    if (!authStore.user) {
      try {
        await authStore.fetchCurrentUser()
      } catch (error) {
        // If fetching user fails, logout and redirect to login
        authStore.setToken(null)
        next({ name: 'login' })
        return
      }
    }

    // Check role requirements
    if (to.meta.roles) {
      const userRole = authStore.user?.role
      if (!userRole || !to.meta.roles.includes(userRole)) {
        console.warn(`Access denied: User role '${userRole}' not in required roles:`, to.meta.roles)
        // Niet stil doorsturen: de gebruiker moet zien dat de rol (of het account) niet klopt
        const roleLabel = roleLabels[userRole] || userRole || 'onbekend'
        const email = authStore.user?.email || 'onbekend account'
        useToast().warning(`Geen toegang tot deze pagina. Je bent ingelogd als ${roleLabel} (${email}).`, { timeout: 8000 })
        next({ name: 'k-numbers' })
        return
      }
    }
  }

  // Redirect authenticated users away from guest pages
  if (to.matched.some(record => record.meta.guest)) {
    if (authStore.isAuthenticated) {
      next({ name: 'k-numbers' })
      return
    }
  }

  next()
})

export default router